import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Publishes a NIP-09 deletion request (kind 5) for a video event.
 *
 * For addressable kinds (34235 / 34236) the `a` tag is included alongside
 * the `e` tag so relays that support addressable deletion honour the request.
 * A `k` tag identifies the kind being deleted as required by NIP-09.
 *
 * After the deletion request is broadcast, the relevant TanStack Query caches
 * are invalidated so the UI reflects the removal immediately.
 */
export function useDeleteVideo() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (event: NostrEvent) => {
      if (!user) throw new Error('Must be logged in to delete a video');
      if (event.pubkey !== user.pubkey) throw new Error('You can only delete your own videos');

      const tags: string[][] = [
        ['e', event.id],
        ['k', String(event.kind)],
      ];

      // For addressable kinds include the `a` coordinate tag so relays can
      // delete all versions of the event, not just this specific one.
      const ADDRESSABLE_KINDS = [34235, 34236];
      if (ADDRESSABLE_KINDS.includes(event.kind)) {
        const d = event.tags.find(([t]) => t === 'd')?.[1] ?? '';
        tags.push(['a', `${event.kind}:${event.pubkey}:${d}`]);
      }

      const deletionEvent = await user.signer.signEvent({
        kind: 5,
        content: 'Deleted by author',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      await nostr.event(deletionEvent, { signal: AbortSignal.timeout(8000) });
      return event;
    },
    onSuccess: (deletedEvent) => {
      // Invalidate all query caches that might contain this event so the UI
      // removes the video immediately without needing a page reload.
      queryClient.invalidateQueries({ queryKey: ['video-feed'] });
      queryClient.invalidateQueries({ queryKey: ['video-feed-author', deletedEvent.pubkey] });
      queryClient.invalidateQueries({ queryKey: ['video-event', deletedEvent.id] });
      queryClient.invalidateQueries({ queryKey: ['shorts-feed'] });
    },
  });
}
